import 'core-js/modules/es.symbol.js';
import 'core-js/modules/es.symbol.description.js';
import 'core-js/modules/es.function.name.js';
import 'core-js/modules/es.object.assign.js';
import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useRef } from 'react';
import { useMenuItem } from '@mui/base';
import classNames from 'classnames';
import { useDragControls, useMotionValue, Reorder } from 'framer-motion';
import { DragHandleVerticalIcon, CloseIcon, ChevronRightIcon, CheckmarkIcon } from '@cognite/cogs-icons';
import { Avatar } from '../avatar/avatar.js';
import { Button } from '../button/button.js';
import { Checkbox } from '../checkbox/checkbox.js';
import { Shortcut } from '../shortcut/shortcut.js';
import { Switch } from '../switch/switch.js';
import { Tooltip } from '../tooltip/tooltip.js';
import { useMenuContext } from './menu-context.js';
import { MenuItemIcon } from './menu-item-icon.js';

// We use this function to understand whether a property allows for displaying // a state for the component with uncontrolled inputs.
var isNotUndefined = function isNotUndefined(prop) {
  return typeof prop !== 'undefined';
};
/**
 * MenuItem
 * =============================================================================
 * The MenuItem component that renders a listbox list item.
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 *
 * ```
 * <Menu.Item hasCheckbox icon={<AddIcon/>} iconPlacement="left">Add item</Menu.Item>
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 */
var MenuItem = /*#__PURE__*/forwardRef(function (props, ref) {
  var avatar = props.avatar,
    children = props.children,
    chip = props.chip,
    description = props.description,
    _props$destructive = props.destructive,
    destructive = _props$destructive === undefined ? false : _props$destructive,
    _props$disabled = props.disabled,
    disabled = _props$disabled === undefined ? false : _props$disabled,
    checkboxProps = props.checkboxProps,
    hasCheckbox = props.hasCheckbox,
    hasSwitch = props.hasSwitch,
    switchProps = props.switchProps,
    href = props.href,
    icon = props.icon,
    value = props.value,
    _props$iconPlacement = props.iconPlacement,
    iconPlacement = _props$iconPlacement === undefined ? 'right' : _props$iconPlacement,
    action = props.action,
    name = props.name,
    onClick = props.onClick,
    _props$toggled = props.toggled,
    toggled = _props$toggled === undefined ? false : _props$toggled,
    _props$showAppendedIc = props.showAppendedIconOnHover,
    showOnHover = _props$showAppendedIc === undefined ? false : _props$showAppendedIc,
    submenuIcon = props.submenuIcon,
    shortcut = props.shortcut,
    hideTooltip = props.hideTooltip,
    keepMenuOpenOnToggle = props.keepMenuOpenOnToggle,
    menuItemTooltipProps = props.menuItemTooltipProps,
    otherProps = __rest(props, ["avatar", "children", "chip", "description", "destructive", "disabled", "checkboxProps", "hasCheckbox", "hasSwitch", "switchProps", "href", "icon", "value", "iconPlacement", "action", "name", "onClick", "toggled", "showAppendedIconOnHover", "submenuIcon", "shortcut", "hideTooltip", "keepMenuOpenOnToggle", "menuItemTooltipProps"]);
  var _a = useMenuContext(),
    hasReorder = _a.hasReorder,
    items = _a.items,
    rootMenuContext = __rest(_a, ["hasReorder", "items"]);
  var dragControls = useDragControls();
  var y = useMotionValue(0);
  var titleRef = useRef(null);
  var _useMenuItem = useMenuItem({
      rootRef: ref,
      disabled: disabled
    }),
    getRootProps = _useMenuItem.getRootProps;
  var extendedClassName = classNames(['cogs-v10', 'cogs-menu-item', destructive && 'cogs-menu-item--destructive', toggled && 'cogs-menu-item--toggled', disabled && 'cogs-menu-item--disabled']);
  var isReorderEnabled = hasReorder && items;
  var isTitleOverflown = function isTitleOverflown() {
    var _a;
    var _ref = (_a = titleRef === null || titleRef === undefined ? undefined : titleRef.current) !== null && _a !== undefined ? _a : {},
      _ref$offsetWidth = _ref.offsetWidth,
      offsetWidth = _ref$offsetWidth === undefined ? 0 : _ref$offsetWidth,
      _ref$scrollWidth = _ref.scrollWidth,
      scrollWidth = _ref$scrollWidth === undefined ? 0 : _ref$scrollWidth;
    return offsetWidth < scrollWidth;
  };
  var title = jsx("span", {
    "data-testid": "cogs-menu-item__content__title",
    className: "cogs-v10 cogs-menu-item__content__title",
    ref: titleRef,
    children: children
  });
  var extendedChildren = jsxs("div", {
    className: "cogs-v10 cogs-menu-item__container",
    children: [iconPlacement === 'left' && jsx(MenuItemIcon, {
      showOnHover: showOnHover,
      icon: icon,
      "data-testid": "cogs-menu-item-icon-left"
    }), isReorderEnabled ? jsx("div", {
      className: "cogs-v10 cogs-menu-item__column",
      children: jsx(DragHandleVerticalIcon, {
        style: {
          cursor: 'grab'
        },
        onPointerDown: function onPointerDown(e) {
          dragControls.start(e);
        }
      })
    }) : null, (hasCheckbox || rootMenuContext.hasCheckbox) && jsx("div", {
      className: "cogs-v10 cogs-menu-item__column",
      children: jsx(Checkbox, Object.assign({
        id: name,
        name: name || '',
        checked: toggled,
        disabled: disabled,
        onChange: function onChange(evt) {
          return onClick && onClick(evt);
        }
      }, checkboxProps))
    }), avatar && jsx("div", {
      className: "cogs-v10 cogs-menu-item__column",
      children: jsx(Avatar, {
        text: avatar === null || avatar === undefined ? undefined : avatar.text,
        image: avatar === null || avatar === undefined ? undefined : avatar.image,
        size: "x-small"
      })
    }), jsxs("div", {
      className: classNames('cogs-v10', 'cogs-menu-item__content', {
        'cogs-menu-item__content__toggled': (hasCheckbox || hasSwitch || rootMenuContext.hasCheckbox || rootMenuContext.hasSwitch) && !disabled
      }),
      children: [!hideTooltip && isTitleOverflown() ? jsx(Tooltip, Object.assign({}, menuItemTooltipProps, {
        content: children,
        children: title
      })) : title, description && jsx("span", {
        className: "cogs-v10 cogs-detail cogs-menu-item__content__description",
        children: description
      })]
    }), chip && jsx("div", {
      className: "cogs-v10 cogs-menu-item__column",
      children: chip
    }), (hasSwitch || rootMenuContext.hasSwitch) && jsx("div", {
      className: "cogs-v10 cogs-menu-item__column",
      children: jsx("div", {
        children: jsx(Switch, Object.assign({
          id: name,
          name: name || '',
          size: "tiny",
          disabled: disabled,
          checked: toggled
        }, switchProps))
      })
    }), iconPlacement === 'right' && jsx(MenuItemIcon, {
      showOnHover: showOnHover,
      icon: icon,
      "data-testid": "cogs-menu-item-icon-right"
    }), shortcut && jsx("div", {
      className: "cogs-v10 cogs-menu-item__column",
      children: jsx(Shortcut, {
        keys: shortcut
      })
    }), action && jsx("div", {
      className: "cogs-menu-item__column cogs-menu-item__action-button__container",
      children: jsx(Button, {
        type: "ghost",
        size: "small",
        disabled: disabled,
        "aria-label": action['aria-label'] || 'Close',
        icon: action.icon || jsx(CloseIcon, {}),
        className: "cogs-menu-item__action-button",
        onClick: function onClick(event) {
          event.stopPropagation();
          if (action.onClick) {
            action.onClick(event);
          }
        },
        "data-testid": "cogs-menu-item-action-button"
      })
    }), submenuIcon && jsx(MenuItemIcon, {
      icon: submenuIcon === true ? jsx(ChevronRightIcon, {}) : submenuIcon
    }), toggled && !hasCheckbox && !hasSwitch && !submenuIcon && jsx(CheckmarkIcon, {})]
  });
  if (isNotUndefined(hasCheckbox) || isNotUndefined(hasSwitch)) {
    var rootProps = getRootProps();
    return jsx("label", Object.assign({
      htmlFor: name,
      disabled: disabled,
      className: extendedClassName
    }, rootProps, otherProps, {
      onClick: keepMenuOpenOnToggle ? function (e) {
        e.stopPropagation();
        rootProps.onClick(e);
      } : rootProps.onClick,
      children: extendedChildren
    }));
  }
  // If we have an href property we want it to take precedence over the button
  // rendering, and instead render an anchor element.
  if (href) {
    return jsx("a", Object.assign({
      href: href,
      className: extendedClassName
    }, getRootProps(), otherProps, {
      onClick: onClick,
      children: extendedChildren
    }));
  }
  if (isReorderEnabled) {
    return jsx(Reorder.Item, Object.assign({
      as: "button",
      dragListener: false,
      dragControls: dragControls,
      value: value,
      style: {
        y: y
      },
      disabled: disabled,
      className: extendedClassName
    }, getRootProps(), {
      onClick: onClick,
      children: extendedChildren
    }));
  }
  // We need to render an interactive component if there if the component passes
  // an onClick. However, since we define a default value to this property,
  // unless we specifically provide a falsy value to onClick, this component
  // will always be a button. Should we consider defaulting to this instead?
  if (onClick) {
    return jsx("button", Object.assign({
      type: "button",
      disabled: disabled,
      className: extendedClassName
    }, getRootProps(), otherProps, {
      onClick: onClick,
      children: extendedChildren
    }));
  }
  // We want to default to rendering a div with minimal props when the component
  // doesn't have more complex composition props.
  return jsx("div", Object.assign({
    className: extendedClassName
  }, getRootProps(), otherProps, {
    children: extendedChildren
  }));
});

export { MenuItem };
